import {MAP_CENTER_KEY, MAP_ZOOM_KEY} from '@shared/constants/storage-keys';
import {ChangeDetectorRef, Component, computed, effect, inject, OnInit, signal} from '@angular/core';
import {ReactiveFormsModule} from '@angular/forms';
import {Router, RouterOutlet} from '@angular/router';
import {MessageService} from 'primeng/api';
import {AutoCompleteModule} from 'primeng/autocomplete';
import {ButtonModule} from 'primeng/button';
import {EditorModule} from 'primeng/editor';
import {InputTextModule} from 'primeng/inputtext';
import {StepsModule} from 'primeng/steps';
import {ToastModule} from 'primeng/toast';
import {environment} from '@environments/environment';
import {TipoObraService} from '@core/services/tipo-obra.service';
import {GoogleMapsLoaderService} from '@core/services/google-maps-loader.service';
import {MapaDemandasService} from '@core/services/mapa-demandas.service';
import {ProgressSpinnerModule} from 'primeng/progressspinner';
import {TooltipModule} from 'primeng/tooltip';
import {GoogleMap, MapAdvancedMarker} from '@angular/google-maps';
import {
    AdicionarDemandaStepsComponent
} from '@pages/demandas/adicionar-demanda/adicionar-demanda-steps/adicionar-demanda-steps.component';
import {toSignal} from '@angular/core/rxjs-interop';
import {DemandaService} from '@core/services/demanda.service';
import {HttpErrorResponse} from '@angular/common/http';
import {DialogComponent} from '@components/dialogs/dialog/dialog.component';
import {IconCheckedSquareComponent} from '@components/icons/icon-checked-square/icon-checked-square.component';
import {AdicionarDemandaStateService} from '@core/services/adicionar-demanda-state.service';
import {
    SelecionarTipoObraDialog
} from '@pages/demandas/adicionar-demanda/selecionar-tipo-obra-dialog/selecionar-tipo-obra-dialog.component';
import {
    InputSelectTipoObra
} from '@pages/demandas/adicionar-demanda/input-select-tipo-obra/input-select-tipo-obra.component';
import {DadosLocalizacaoDemanda} from '@core/models/demanda';
import {IconBoldCloseSquareComponent} from '@components/icons/icon-bold-close-square/icon-bold-close-square.component';

@Component({
    selector: 'app-adicionar-demanda',
    standalone: true,
    providers: [AdicionarDemandaStateService],
    imports: [
        ReactiveFormsModule,
        ButtonModule,
        InputTextModule,
        StepsModule,
        EditorModule,
        AutoCompleteModule,
        ToastModule,
        ProgressSpinnerModule,
        TooltipModule,
        GoogleMap,
        MapAdvancedMarker,
        AdicionarDemandaStepsComponent,
        RouterOutlet,
        DialogComponent,
        IconCheckedSquareComponent,
        SelecionarTipoObraDialog,
        InputSelectTipoObra,
        IconBoldCloseSquareComponent
    ],
    templateUrl: './adicionar-demanda.component.html',
    styleUrl: './adicionar-demanda.component.css'
})
export class AdicionarDemandaComponent implements OnInit {
    protected readonly mapId = environment.google.mapId;

    protected zoom = 12;
    protected center: google.maps.LatLngLiteral = {lat: -23.5505, lng: -46.6333};
    protected mapOptions: google.maps.MapOptions = {};

    protected readonly isMapaCarregado = signal(false);
    protected showSelectTipoObraDialog = false;
    protected showAlertaSelecioneTipoObra = signal(true);
    protected showDadosLocalizacaoDialog = false;
    protected showAlertaLocalizacaoDuplicadaRaioTipoObra = false;
    protected showAlertaLocalizacaoForaArtesp = false;
    protected dadosLocalizacaoDemandaSelecionada = signal<DadosLocalizacaoDemanda | null>(null)
    protected readonly adicionarDemandaState = inject(AdicionarDemandaStateService);
    protected readonly selectedTipoObraId = signal<number | null>(null);
    protected readonly posicaoPinSelecionado = signal<google.maps.LatLngLiteral | null>(null);
    private readonly mapaDemandasService = inject(MapaDemandasService);
    private readonly tipoObraService = inject(TipoObraService);
    protected readonly pinSelecionadoContent = computed(() =>
        this.tipoObraService.buildPinIcon(this.selectedTipoObraId() ?? undefined)
    );
    private readonly googleMapsLoader = inject(GoogleMapsLoaderService);
    private readonly demandaService = inject(DemandaService);
    private readonly pins = toSignal(this.mapaDemandasService.pins$, {initialValue: []});
    protected readonly pinsFiltrados = computed(() => {
        const filtro = this.selectedTipoObraId();
        const allPins = this.pins() ?? [];
        const pins = filtro ? allPins.filter(p => p.idTipoObra === filtro) : allPins;
        return pins.map(p => ({...p, content: this.tipoObraService.buildPinIcon(p.idTipoObra)}));
    });

    constructor(
        private readonly router: Router,
        private readonly messageService: MessageService,
        private readonly cdr: ChangeDetectorRef,
    ) {
        effect(() => {
            if (this.adicionarDemandaState.activeStep() !== 0) {
                this.showAlertaSelecioneTipoObra.set(false);
            }
        });

        effect(() => {
            if (!this.adicionarDemandaState.dadosLocalizacao()) {
                this.posicaoPinSelecionado.set(null);
            }
        });
    }

    ngOnInit(): void {
        this.restoreStoredZoomAndCenter();
        this.carregarMapa();
        this.fetchDemandas();
    }

    onMapInitialized(map: google.maps.Map): void {
        this.mapOptions = this.mapaDemandasService.buildMapOptions();
        this.cdr.detectChanges();
        this.mapaDemandasService.drawContornoSaoPaulo(map);
    }

    handleMapClick(event: google.maps.MapMouseEvent): void {
        const tipoObraId = this.selectedTipoObraId();
        if (!event.latLng || !tipoObraId) return;

        const latitude = event.latLng.lat();
        const longitude = event.latLng.lng();

        this.demandaService
            .getDemandaMalha(latitude, longitude, tipoObraId)
            .subscribe({
                next: (response: DadosLocalizacaoDemanda) => {
                    this.posicaoPinSelecionado.set({lat: latitude, lng: longitude});
                    this.showDadosLocalizacaoDemandaSelecionada(response);
                },
                error: (err: HttpErrorResponse) => {
                    if (err.status === 500 && err.error.message.includes("Já existe uma demanda dentro do raio informado")) {
                        this.showAlertaLocalizacaoDuplicadaRaioTipoObra = true;
                    } else if (err.status === 404 && err.error.message.includes('Nenhuma rodovia sob a jurisdição da ARTESP encontrada na localização informada')) {
                        this.showAlertaLocalizacaoForaArtesp = true;
                    } else {
                        this.messageService.add({
                            severity: 'error',
                            summary: 'Erro!',
                            detail: 'Ocorreu um erro ao buscar os dados da localização. Por favor, tente novamente.'
                        })
                        console.error('[AdicionarDemanda]: Erro ao selecionar localização da demanda: ', err);
                    }
                }
            });
    }

    showDadosLocalizacaoDemandaSelecionada(dadosLocalizacao: DadosLocalizacaoDemanda): void {
        this.dadosLocalizacaoDemandaSelecionada.update(() => dadosLocalizacao)
        this.showDadosLocalizacaoDialog = true;
    }

    limparLocalizacaoSelecionada() {
        this.posicaoPinSelecionado.set(null);
    }

    confirmarLocalizacaoDemandaSelecionada() {
        this.showDadosLocalizacaoDialog = false;
        const dados = this.dadosLocalizacaoDemandaSelecionada();
        const tipoObraId = this.selectedTipoObraId();
        if (dados && tipoObraId) {
            this.adicionarDemandaState.setLocalizacao(dados, tipoObraId);
            if (this.adicionarDemandaState.descricaoDemanda()) {
                this.adicionarDemandaState.irParaStep(2);
                this.router.navigate(['/demandas/adicionar/anexos']);
            } else {
                this.adicionarDemandaState.irParaStep(1);
                this.router.navigate(['/demandas/adicionar/descricao']);
            }
        }
    }

    onSelectTipoObra(id: number): void {
        this.selectedTipoObraId.set(id);
    }

    onFilterByTipoObra(id: number | null): void {
        this.selectedTipoObraId.set(id);
        this.limparLocalizacaoSelecionada();
    }

    private restoreStoredZoomAndCenter(): void {
        const storedZoom = sessionStorage.getItem(MAP_ZOOM_KEY);
        if (storedZoom) this.zoom = Number.parseInt(storedZoom);

        const storedCenter = sessionStorage.getItem(MAP_CENTER_KEY);
        if (storedCenter) this.center = JSON.parse(storedCenter);
    }

    private carregarMapa(): void {
        this.googleMapsLoader.load().then(() => this.isMapaCarregado.set(true));
    }

    private fetchDemandas(): void {
        this.mapaDemandasService.loadDemandas();
    }
}
